/**
 * Which build this is, and the handful of things that differ between them.
 *
 * There are three: the site at kaelmoor.io, the Steam bundle, and the copy
 * CrazyGames hosts on its own CDN. They are one codebase and one server — the
 * *same* worlds, the same lobbies, the same match — and this file is the whole
 * list of what is not identical.
 *
 * Everything here is derived from `__PLATFORM__` and `__API_BASE__`, which
 * vite.config.ts stamps in as literals. That matters more than it looks: a
 * comparison against a literal constant-folds, so `if (ON_CRAZYGAMES)` in a web
 * build is not a test that fails at runtime, it is code the bundler deletes. The
 * web bundle therefore contains no portal logic at all, and the portal bundle
 * contains no advertising — which is what both Valve and CrazyGames require, and
 * what "the current version does not change" means in practice.
 */

export type Platform = 'web' | 'steam' | 'crazygames';

export const PLATFORM = __PLATFORM__ as Platform;

/** The build CrazyGames hosts. Constant-folds; guard portal-only code with it. */
export const ON_CRAZYGAMES = PLATFORM === 'crazygames';

/**
 * Origin of the game server, or `''` when the page is served by it.
 *
 * On kaelmoor.io the Node process serves the HTML *and* runs the war, so every
 * path is same-origin and this stays empty — which is also what keeps
 * `npm run dev` working against localhost. A portal build is loaded from the
 * portal's domain and has to be told where the war actually is.
 */
export const API_BASE = __API_BASE__;

/**
 * Absolute URL for a server path, or the path itself when same-origin.
 *
 * Use for anything the *game server* answers: `/rooms`, `/backdrop.png`,
 * `/health`. Do **not** use it for `/wiki.html`, `/terms-of-service.html` or
 * `/privacy-policy.html` — those are static files that ship inside every bundle,
 * so they must stay relative or a portal build would fetch them across the
 * network for no reason.
 */
export function apiUrl(path: string): string {
  return API_BASE ? API_BASE + path : path;
}

/**
 * Where to open the WebSocket.
 *
 * Same-origin builds derive the scheme from the page, which is what makes a
 * plain `http://localhost:5173` dev session work while the live site gets
 * `wss://`. A portal build ignores the page's origin entirely — that origin is
 * the portal's, and there is no game server behind it.
 */
export function wsUrl(): string {
  if (API_BASE) return API_BASE.replace(/^http/, 'ws') + '/ws';
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}

/**
 * Whether the game may offer its own full-screen button.
 *
 * It may not on CrazyGames: the portal supplies the frame and its own
 * full-screen control, and their requirements say custom in-game full-screen
 * buttons are prohibited because a second one fights the first. The button is
 * removed from the document and the `G` key does nothing, rather than the button
 * being left there to throw an error when the portal's iframe refuses.
 */
export const FULLSCREEN_ALLOWED = !ON_CRAZYGAMES;

/**
 * Links that must not appear in a portal build.
 *
 * CrazyGames allows community and documentation links in the menu — Discord, a
 * wiki, a privacy policy — and forbids exactly one thing: a link that leads to a
 * **playable** copy of the game. That is the rule about traffic, not about
 * domains, so `kaelmoor.io/privacy-policy.html` is fine and a "play at
 * kaelmoor.io" button is not. There is no such button today; this exists so that
 * if one is ever added, it is added knowing there is a second build it must be
 * kept out of.
 */
export const MAY_LINK_TO_OWN_SITE = !ON_CRAZYGAMES;

/**
 * **Whether this is a finger or a mouse.**
 *
 * Not "is it Android or iOS" — that is a question about a badge, and it gets
 * the answer wrong at both ends: a Windows tablet is a touch device and an
 * Android with a mouse plugged in is not. What the interface actually needs to
 * know is whether the pointer is *coarse* (a fingertip, roughly ten
 * millimetres) and whether the screen can be touched at all.
 *
 * Read once at boot rather than per event. A hybrid laptop can be both, and
 * switching the whole layout under somebody mid-match because they brushed the
 * screen would be worse than picking one and staying with it. The user agent is
 * consulted only as a fallback for browsers that report neither.
 */
export const IS_TOUCH = (() => {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  const touchable = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;
  if (coarse && touchable) return true;
  // last resort: a phone that lies about its pointer is still a phone
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) && touchable;
})();

/**
 * **How long a mouse event is suspected of being a ghost.**
 *
 * After a touch, browsers replay the gesture as *compatibility mouse events* so
 * that pages written for a mouse keep working. This page is not one of them,
 * and the replay is not harmless here: the wheel opens under the fingertip on
 * `pointerup`, so the phantom click that follows lands on the wheel that tap
 * just created — on your own ground that is the middle of it, whose whole job
 * is to close the menu. The wheel appeared and vanished in one gesture, and
 * Attack could never be pressed because the thing holding it had already gone.
 *
 * `preventDefault` on the touch suppresses the replay in most browsers. This is
 * the belt to that pair of braces, for the ones where it does not, and it lives
 * here rather than in `input.ts` because `ui.ts` has to make the same judgement
 * about the same events and the two must not answer differently.
 */
export const TOUCH_ECHO_MS = 700;

let lastTouchAt = -1e9;

/** Called from every real touch, so the echo window is measured from the last. */
export function noteTouch() {
  lastTouchAt = performance.now();
}

/** True while any mouse event is more likely a replayed touch than a mouse. */
export function touchEcho(): boolean {
  return performance.now() - lastTouchAt < TOUCH_ECHO_MS;
}

/**
 * Stamp the device on the document, so the stylesheet can lay itself out for a
 * thumb without a single element being measured in JavaScript.
 */
export function markDevice() {
  if (!IS_TOUCH) return;
  document.body.dataset.touch = '1';
  /*
   * The build palette leaves the document rather than being hidden. Everything
   * in it is in the right-click wheel already, reachable with one tap on the
   * tile the building should stand on — which is a better gesture than "choose
   * the thing, then hunt for the place", and on a phone the palette costs a
   * third of the map to offer the worse one.
   */
  document.getElementById('build-card')?.remove();
  /*
   * The team pings go too. They are a *coordination* tool for a team that is
   * talking about a map — three buttons that each cost a tap and a moment of
   * looking away — and on a phone the screen they sit on is the screen the war
   * is being fought on. A player on a touch device is not the one directing
   * anybody; they are the one with four hundred pixels of continent.
   */
  document.getElementById('ping-box')?.remove();
}
